import { Type } from '@angular/core';
import { FlareCommands } from '@common/flare/flare-commands';
import { FlareSchema } from '@common/flare/flare-schema';
import { ComponentPluginView } from '@portal-core/text-editor/plugin-views/component.plugin-view';
import { ToolbarPopupPluginViewComponentBase } from '@portal-core/text-editor/plugin-views/util/toolbar-popup-plugin-view-component.base';
import { ElementOverlayService } from '@portal-core/ui/overlay/services/element.overlay.service';
import { EditorProps, EditorView } from 'prosemirror-view';
import { Observable, Subscription } from 'rxjs';

/**
 * The base class for toolbar popup plugin views (e.g. the equation and node/snippet toolbars) -
 * handles the readonly-state subscription, the shared Escape-key handling, and the delete-node
 * behavior common to every toolbar popup. Subclasses implement updateComponent() and subscribe
 * to any of their own extra component events in onComponentCreated() (adding to this.subscriptions
 * so they're torn down together with the shared ones).
 */
export abstract class ToolbarPopupPluginViewBase<T extends ToolbarPopupPluginViewComponentBase> extends ComponentPluginView<T> {
  protected flareCommands: FlareCommands;
  protected subscriptions: Subscription;
  protected isReadonly: boolean;

  private readonlySubscription: Subscription;

  props: EditorProps = {
    // Listens for the escape key to be pressed to close the popup
    handleKeyDown: (_editorView: EditorView, event: KeyboardEvent): boolean => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        return true;
      } else {
        return false;
      }
    }
  };

  constructor(componentType: Type<T>, elementOverlayService: ElementOverlayService, isReadonly$: Observable<boolean>) {
    super(componentType, elementOverlayService);
    this.readonlySubscription = isReadonly$.subscribe(isReadonly => this.isReadonly = isReadonly);
  }

  onComponentCreated() {
    this.subscriptions = this.component.removeNode.subscribe(this.onRemoveNode.bind(this));
    this.subscriptions.add(this.readonlySubscription);
  }

  onComponentDestroyed() {
    this.subscriptions.unsubscribe();
  }

  onRemoveNode() {
    this.flareCommands = this.flareCommands ?? new FlareCommands(this.editorView.state.schema as FlareSchema);
    this.flareCommands.deleteSelection(this.editorView.state, this.editorView.dispatch, this.editorView);
    this.closePopup();
  }

  closePopup() {
    this.editorView.focus();
    this.component.closePopup();
  }
}
